import { useEffect, useRef, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import {
  OPERATIONS_ALERT_DEFAULTS,
  OPERATIONS_ALERT_MAX_RECIPIENTS,
  operationsAlertSettingsFieldsSchema,
  operationsAlertSettingsUpdateSchema,
  type OperationsAlertSettings,
} from '@erp/shared';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Skeleton } from '@/components/ui/skeleton';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogCancel,
  AlertDialogAction,
} from '@/components/ui/alert-dialog';
import { FormField } from '@/components/common/form-field';
import { usePermissions } from '@/features/auth';
import { InlineHelp } from '@/features/help';
import { ApiError } from '@/lib/api-client';
import { formatDateTime } from '@/lib/formatters';
import {
  useOperationsAlertSettings,
  useUpdateOperationsAlertSettings,
} from '../api/use-operations-alerts';
import { OperationsAlertHistory } from '../components/operations-alert-history';

const NUMBER_FIELDS = [
  'minimumIntervalMinutes',
  'maxPerMinute',
  'maxPerHour',
  'maxPerDay',
  'repeatIntervalMinutes',
] as const;
function recipients(value: string): string[] {
  return value
    .split(/[,;\n]+/u)
    .map((entry) => entry.trim().toLowerCase())
    .filter(Boolean);
}
const formSchema = z
  .object({
    enabled: z.boolean(),
    recipients: z.string(),
    version: z.number(),
    minimumIntervalMinutes: z.number({ invalid_type_error: 'operationsAlerts.validation.number' }),
    maxPerMinute: z.number({ invalid_type_error: 'operationsAlerts.validation.number' }),
    maxPerHour: z.number({ invalid_type_error: 'operationsAlerts.validation.number' }),
    maxPerDay: z.number({ invalid_type_error: 'operationsAlerts.validation.number' }),
    repeatIntervalMinutes: z.number({ invalid_type_error: 'operationsAlerts.validation.number' }),
  })
  .superRefine((value, context) => {
    const parsed = operationsAlertSettingsUpdateSchema.safeParse({
      ...value,
      recipients: recipients(value.recipients),
    });
    if (parsed.success) return;
    for (const issue of parsed.error.issues) {
      const message = issue.message.startsWith('operationsAlerts.')
        ? issue.message
        : issue.path[0] === 'recipients'
          ? issue.code === 'too_big' && issue.path.length === 1
            ? 'operationsAlerts.validation.tooMany'
            : 'operationsAlerts.validation.email'
          : 'operationsAlerts.validation.number';
      context.addIssue({ code: z.ZodIssueCode.custom, path: issue.path.slice(0, 1), message });
    }
  });
type AlertForm = z.infer<typeof formSchema>;
function formValues(settings: OperationsAlertSettings): AlertForm {
  return {
    enabled: settings.enabled,
    recipients: settings.recipients.join('\n'),
    minimumIntervalMinutes: settings.minimumIntervalMinutes,
    maxPerMinute: settings.maxPerMinute,
    maxPerHour: settings.maxPerHour,
    maxPerDay: settings.maxPerDay,
    repeatIntervalMinutes: settings.repeatIntervalMinutes,
    version: settings.version,
  };
}

export function OperationsAlertsRoute(): JSX.Element {
  const { t, i18n } = useTranslation();
  const { can } = usePermissions();
  const editable = can('operations:read') && can('operations:configure');
  const settings = useOperationsAlertSettings();
  const update = useUpdateOperationsAlertSettings();
  const initialized = useRef(false);
  const [submitError, setSubmitError] = useState<'conflict' | 'saveError' | null>(null);
  const [saved, setSaved] = useState(false);
  const [reloadOpen, setReloadOpen] = useState(false);
  const { control, register, handleSubmit, reset, setFocus, formState } = useForm<AlertForm>({
    resolver: zodResolver(formSchema),
    defaultValues: { ...OPERATIONS_ALERT_DEFAULTS, recipients: '', version: 0 },
    shouldFocusError: true,
  });
  const { isDirty, errors } = formState;
  useEffect(() => {
    if (!settings.data) return;
    // Keep draft values and their original lock version together until an explicit reload.
    if (!initialized.current || !isDirty) reset(formValues(settings.data));
    initialized.current = true;
  }, [settings.data, isDirty, reset]);

  const submit = handleSubmit(
    async (value) => {
      if (!editable || update.isPending || submitError === 'conflict') return;
      setSaved(false);
      try {
        const data = await update.mutateAsync({
          ...value,
          recipients: recipients(value.recipients),
        });
        reset(formValues(data));
        setSubmitError(null);
        setSaved(true);
        toast.success(t('operationsAlerts.saved'));
      } catch (cause) {
        setSubmitError(
          cause instanceof ApiError && cause.status === 409 ? 'conflict' : 'saveError',
        );
        if (cause instanceof ApiError && cause.status === 409) void settings.refetch();
      }
    },
    () => {
      setSaved(false);
    },
  );
  useEffect(() => {
    const keyboardSave = (event: KeyboardEvent): void => {
      if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 's') {
        event.preventDefault();
        if (editable && initialized.current && !update.isPending) void submit();
      }
    };
    window.addEventListener('keydown', keyboardSave);
    return () => window.removeEventListener('keydown', keyboardSave);
  }, [editable, submit, update.isPending]);

  const loadCurrent = async (): Promise<void> => {
    const current = await settings.refetch();
    if (current.isSuccess && current.data) {
      reset(formValues(current.data));
      setSubmitError(null);
      setSaved(false);
    }
  };
  const fieldError = (field: keyof AlertForm): string | undefined => {
    const message = errors[field]?.message;
    if (!message) return undefined;
    const limits = NUMBER_FIELDS.find((entry) => entry === field);
    const numericSchema = limits ? operationsAlertSettingsFieldsSchema.shape[limits] : undefined;
    return t(message, {
      max: numericSchema?.maxValue ?? OPERATIONS_ALERT_MAX_RECIPIENTS,
      min: numericSchema?.minValue ?? 0,
    });
  };
  const fieldsWithErrors = Object.keys(errors).filter(
    (field): field is keyof AlertForm => field in formSchema.innerType().shape,
  );

  return (
    <div className="space-y-6">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold">{t('operationsAlerts.title')}</h1>
          <p className="mt-1 text-sm text-muted-foreground">{t('operationsAlerts.description')}</p>
        </div>
        <span className="[&>button]:h-11 [&>button]:w-11">
          <InlineHelp articleId="settings.operations-alerts" label={t('operationsAlerts.help')} />
        </span>
      </header>
      <Card>
        <CardHeader>
          <CardTitle>{t('operationsAlerts.configuration')}</CardTitle>
          <CardDescription>{t('operationsAlerts.defaultsHint')}</CardDescription>
        </CardHeader>
        <CardContent>
          {settings.isError && settings.data && (
            <div
              role="alert"
              className="mb-4 space-y-3 rounded-md border border-destructive/50 p-3"
            >
              <p>{t('operationsAlerts.settingsError')}</p>
              <Button
                type="button"
                variant="outline"
                className="min-h-11"
                onClick={() => void settings.refetch()}
              >
                {t('common.retry')}
              </Button>
            </div>
          )}
          {settings.isPending ? (
            <div
              role="status"
              aria-label={t('operationsAlerts.loadingSettings')}
              className="space-y-4"
            >
              <Skeleton className="h-11 w-48" />
              <Skeleton className="h-28 w-full" />
              <Skeleton className="h-11 w-full" />
            </div>
          ) : settings.isError && !settings.data ? (
            <div role="alert" className="space-y-3">
              <p>{t('operationsAlerts.settingsError')}</p>
              <Button
                type="button"
                variant="outline"
                className="min-h-11"
                onClick={() => void settings.refetch()}
              >
                {t('common.retry')}
              </Button>
            </div>
          ) : (
            <form className="space-y-5" noValidate onSubmit={(event) => void submit(event)}>
              <div className="flex flex-wrap items-center justify-between gap-3">
                <Controller
                  control={control}
                  name="enabled"
                  render={({ field }) => (
                    <div className="flex min-h-11 items-center gap-3">
                      <Switch
                        className="h-11 w-16 [&>span]:h-9 [&>span]:w-9"
                        id="alerts-enabled"
                        checked={field.value}
                        onCheckedChange={field.onChange}
                        disabled={!editable || update.isPending}
                        aria-describedby="alerts-enabled-description"
                      />
                      <label
                        htmlFor="alerts-enabled"
                        className="cursor-pointer text-sm font-medium"
                      >
                        {t('operationsAlerts.enabled')}
                      </label>
                    </div>
                  )}
                />
                <Badge variant={settings.data?.smtpConfigured ? 'success' : 'warning'}>
                  {t(
                    settings.data?.smtpConfigured
                      ? 'operationsAlerts.smtpConfigured'
                      : 'operationsAlerts.smtpMissing',
                  )}
                </Badge>
              </div>
              <p id="alerts-enabled-description" className="text-sm text-muted-foreground">
                {t('operationsAlerts.infrastructureHint')}
              </p>
              {!editable && (
                <p className="rounded-md border border-border bg-muted/40 p-3 text-sm">
                  {t('operationsAlerts.readOnly')}
                </p>
              )}
              {Object.keys(errors).length > 0 && (
                <div role="alert" className="rounded-md border border-destructive/50 p-3 text-sm">
                  <p className="font-medium">{t('operationsAlerts.validation.summary')}</p>
                  <ul className="mt-2 space-y-1">
                    {fieldsWithErrors.map((field) => (
                      <li key={field}>
                        <button
                          type="button"
                          className="min-h-11 text-start underline"
                          onClick={() => setFocus(field)}
                        >
                          {t(`operationsAlerts.fields.${field}`)}
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              <FormField
                id="alerts-recipients"
                label={t('operationsAlerts.fields.recipients')}
                description={t('operationsAlerts.recipientsHint', {
                  max: OPERATIONS_ALERT_MAX_RECIPIENTS,
                })}
                error={fieldError('recipients')}
              >
                <Textarea
                  id="alerts-recipients"
                  {...register('recipients')}
                  rows={3}
                  disabled={!editable || update.isPending}
                  aria-invalid={Boolean(errors.recipients)}
                  aria-describedby={`alerts-recipients-description${errors.recipients ? ' alerts-recipients-error' : ''}`}
                  autoComplete="off"
                  spellCheck={false}
                />
              </FormField>
              <div className="grid gap-5 sm:grid-cols-2 xl:grid-cols-3">
                {NUMBER_FIELDS.map((field) => (
                  <FormField
                    key={field}
                    id={`alerts-${field}`}
                    label={t(`operationsAlerts.fields.${field}`)}
                    description={t(`operationsAlerts.hints.${field}`)}
                    error={fieldError(field)}
                  >
                    <Input
                      id={`alerts-${field}`}
                      type="number"
                      inputMode="numeric"
                      step="1"
                      min={operationsAlertSettingsFieldsSchema.shape[field].minValue ?? undefined}
                      max={operationsAlertSettingsFieldsSchema.shape[field].maxValue ?? undefined}
                      className="min-h-11"
                      {...register(field, { valueAsNumber: true })}
                      disabled={!editable || update.isPending}
                      aria-invalid={Boolean(errors[field])}
                      aria-describedby={`alerts-${field}-description${errors[field] ? ` alerts-${field}-error` : ''}`}
                    />
                  </FormField>
                ))}
              </div>
              <p className="text-sm text-muted-foreground">{t('operationsAlerts.countingHint')}</p>
              {submitError && (
                <div
                  role="alert"
                  className="space-y-3 rounded-md border border-destructive/50 p-3 text-sm"
                >
                  <p>{t(`operationsAlerts.${submitError}`)}</p>
                  {submitError === 'conflict' && (
                    <Button
                      type="button"
                      variant="outline"
                      className="min-h-11"
                      disabled={settings.isFetching}
                      onClick={() => setReloadOpen(true)}
                    >
                      {t('operationsAlerts.loadCurrent')}
                    </Button>
                  )}
                </div>
              )}
              {saved && (
                <p role="status" className="text-sm">
                  {t('operationsAlerts.saved')}
                </p>
              )}
              <div className="flex flex-wrap items-center gap-3">
                {editable && (
                  <Button
                    type="submit"
                    className="min-h-11"
                    disabled={update.isPending || submitError === 'conflict'}
                  >
                    {t(update.isPending ? 'operationsAlerts.saving' : 'operationsAlerts.save')}
                  </Button>
                )}
                <Button
                  type="button"
                  variant="outline"
                  className="min-h-11"
                  disabled={settings.isFetching || update.isPending}
                  onClick={() => void settings.refetch()}
                >
                  {t('operationsAlerts.refreshSettings')}
                </Button>
                {isDirty && (
                  <span className="text-sm text-muted-foreground">
                    {t('operationsAlerts.unsaved')}
                  </span>
                )}
                {settings.data?.updatedAt && (
                  <span className="text-sm text-muted-foreground">
                    {t('operationsAlerts.updatedAt', {
                      date: formatDateTime(settings.data.updatedAt, i18n.language),
                    })}
                  </span>
                )}
              </div>
            </form>
          )}
        </CardContent>
      </Card>
      <OperationsAlertHistory />
      <AlertDialog open={reloadOpen} onOpenChange={setReloadOpen}>
        <AlertDialogContent>
          <AlertDialogTitle>{t('operationsAlerts.loadCurrent')}</AlertDialogTitle>
          <AlertDialogDescription>
            {t('operationsAlerts.discardDescription')}
          </AlertDialogDescription>
          <AlertDialogFooter>
            <AlertDialogCancel className="min-h-11">{t('common.cancel')}</AlertDialogCancel>
            <AlertDialogAction className="min-h-11" onClick={() => void loadCurrent()}>
              {t('operationsAlerts.discardAndLoad')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
